import React from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useAdminOrders, useInventoryData } from '../../lib/admin-hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency, formatRelativeTime, getStatusColor, getStatusLabel } from '../../lib/utils';
import { ShoppingBag, Clock, CheckCircle2, AlertTriangle, Play, Pause } from 'lucide-react';
import { Button } from '../../components/ui/Button';
export function DashboardPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    settings,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    orders,
    loading: ordersLoading,
    updateOrderStatus
  } = useAdminOrders(restaurant?.id);
  const {
    alerts,
    loading: invLoading
  } = useInventoryData(restaurant?.id);
  // Get tenant context for role-aware layout
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-480">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-481" />
      </div>;
  }
  const todayOrders = orders.filter(o => new Date(o.created_at).toDateString() === new Date().toDateString());
  const newOrders = orders.filter(o => o.status === 'new');
  const preparingOrders = orders.filter(o => o.status === 'preparing');
  const readyOrders = orders.filter(o => o.status === 'ready');
  const todayRevenue = todayOrders.reduce((sum, o) => sum + o.total, 0);
  // Derive alert counts from row-level v_inventory_alerts (real view uses uppercase values)
  const criticalAlerts = alerts.filter(a => a.alert_level === 'CRITICAL');
  const lowAlerts = alerts.filter(a => a.alert_level === 'LOW');
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-482">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-483">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-484">
          <div data-id="element-485">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-486">Dashboard</h1>
            <p className="text-gray-500" data-id="element-487">Resumen de operaciones de hoy</p>
          </div>
          <div className="flex items-center gap-3" data-id="element-488">
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-gray-200 shadow-sm" data-id="element-489">
              <span className="text-sm font-medium text-gray-600" data-id="element-490">Cocina:</span>
              {settings?.kitchen_paused ? <Badge variant="danger" className="flex items-center gap-1" data-id="element-491">
                  <Pause className="w-3 h-3" data-id="element-492" /> Pausada
                </Badge> : <Badge variant="success" className="flex items-center gap-1" data-id="element-493">
                  <Play className="w-3 h-3" data-id="element-494" /> Activa
                </Badge>}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-495">
          <Card data-id="element-496">
            <CardContent className="p-6" data-id="element-497">
              <div className="flex items-center justify-between mb-4" data-id="element-498">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-499">
                  <ShoppingBag className="w-5 h-5 text-blue-600" data-id="element-500" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-501">
                  {todayOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-502">Pedidos Hoy</p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-503">
                {formatCurrency(todayRevenue)}
              </p>
            </CardContent>
          </Card>

          <Card data-id="element-504">
            <CardContent className="p-6" data-id="element-505">
              <div className="flex items-center justify-between mb-4" data-id="element-506">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-507">
                  <Clock className="w-5 h-5 text-amber-600" data-id="element-508" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-509">
                  {newOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-510">
                Nuevos / Pendientes
              </p>
            </CardContent>
          </Card>

          <Card data-id="element-511">
            <CardContent className="p-6" data-id="element-512">
              <div className="flex items-center justify-between mb-4" data-id="element-513">
                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center" data-id="element-514">
                  <ChefHatIcon className="w-5 h-5 text-purple-600" data-id="element-515" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-516">
                  {preparingOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-517">
                En Preparación
              </p>
            </CardContent>
          </Card>

          <Card data-id="element-518">
            <CardContent className="p-6" data-id="element-519">
              <div className="flex items-center justify-between mb-4" data-id="element-520">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-521">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" data-id="element-522" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-523">
                  {readyOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-524">
                Listos para Entrega
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-525">
          {/* Recent Orders */}
          <Card className="lg:col-span-2" data-id="element-526">
            <CardHeader data-id="element-527">
              <CardTitle data-id="element-528">Pedidos Recientes</CardTitle>
            </CardHeader>
            <CardContent data-id="element-529">
              {ordersLoading ? <div className="space-y-4" data-id="element-530">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" data-id="element-531" />)}
                </div> : orders.length > 0 ? <div className="overflow-x-auto" data-id="element-532">
                  <table className="w-full text-sm text-left" data-id="element-533">
                    <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-534">
                      <tr data-id="element-535">
                        <th className="px-4 py-3 rounded-l-lg" data-id="element-536">Pedido</th>
                        <th className="px-4 py-3" data-id="element-537">Cliente</th>
                        <th className="px-4 py-3" data-id="element-538">Tipo</th>
                        <th className="px-4 py-3" data-id="element-539">Total</th>
                        <th className="px-4 py-3" data-id="element-540">Estado</th>
                        <th className="px-4 py-3 rounded-r-lg" data-id="element-541">Acción</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-542">
                      {orders.slice(0, 10).map(order => <tr key={order.id} className="border-b border-gray-50 last:border-0" data-id="element-543">
                          <td className="px-4 py-3 font-medium text-gray-900" data-id="element-544">
                            #{order.order_number}
                            <div className="text-xs text-gray-500 font-normal" data-id="element-545">
                              {formatRelativeTime(order.created_at)}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-546">{order.customer_name}</td>
                          <td className="px-4 py-3" data-id="element-547">
                            <Badge variant="default" data-id="element-548">
                              {order.delivery_type === 'delivery' ? 'Delivery' : order.delivery_type === 'pickup' ? 'Para llevar' : 'Mesa'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 font-medium" data-id="element-549">
                            {formatCurrency(order.total)}
                          </td>
                          <td className="px-4 py-3" data-id="element-550">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`} data-id="element-551">
                              {getStatusLabel(order.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3" data-id="element-552">
                            {order.status === 'new' && <Button size="sm" onClick={() => updateOrderStatus(order.id, 'accepted')} data-id="element-553">
                                Aceptar
                              </Button>}
                            {order.status === 'accepted' && <Button size="sm" variant="secondary" onClick={() => updateOrderStatus(order.id, 'preparing')} data-id="element-554">
                                Preparar
                              </Button>}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div> : <div className="text-center py-8 text-gray-500" data-id="element-555">
                  No hay pedidos recientes
                </div>}
            </CardContent>
          </Card>

          {/* Alerts & Info */}
          <div className="space-y-6" data-id="element-556">
            <Card data-id="element-557">
              <CardHeader data-id="element-558">
                <CardTitle className="flex items-center gap-2" data-id="element-559">
                  <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-560" />
                  Alertas de Inventario
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-561">
                {invLoading ? <Skeleton className="h-20 w-full" data-id="element-562" /> : alerts.length > 0 ? <div className="space-y-4" data-id="element-563">
                    {criticalAlerts.length > 0 && <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100" data-id="element-564">
                        <span className="text-sm font-medium text-red-800" data-id="element-565">
                          Stock Crítico
                        </span>
                        <Badge variant="danger" data-id="element-566">{criticalAlerts.length}</Badge>
                      </div>}
                    {lowAlerts.length > 0 && <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100" data-id="element-567">
                        <span className="text-sm font-medium text-amber-800" data-id="element-568">
                          Stock Bajo
                        </span>
                        <Badge variant="warning" data-id="element-569">{lowAlerts.length}</Badge>
                      </div>}
                    <div className="space-y-2 mt-2" data-id="element-570">
                      {alerts.slice(0, 5).map(alert => <div key={alert.inventory_item_id} className="flex justify-between text-sm" data-id="element-571">
                          <span className="text-gray-700 truncate" data-id="element-572">
                            {alert.ingredient_name}
                          </span>
                          <span className="text-gray-500 shrink-0 ml-2" data-id="element-573">
                            {alert.current_stock} / {alert.minimum_stock}{' '}
                            {alert.unit}
                          </span>
                        </div>)}
                    </div>
                  </div> : <p className="text-sm text-gray-500" data-id="element-574">
                    No hay alertas de inventario.
                  </p>}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>;
}
// Helper icon
function ChefHatIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-575">
      <path d="M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589c-.345 0-.682.041-1.005.115-.428-1.833-2.136-3.134-4.088-3.134s-3.66 1.301-4.088 3.134c-.323-.074-.66-.115-1.005-.115a4 4 0 0 0-2.134 7.589c.411.197.727.584.727 1.041V20a1 1 0 0 0 1 1h12z" data-id="element-576" />
      <path d="M6 17h12" data-id="element-577" />
    </svg>;
}